import { useMemo } from 'react';
import { useScript } from 'usehooks-ts';
import type { UseScriptStatus } from 'usehooks-ts';
import { useSLTConfig } from '../../slt/config/SLTConfigProvider';
import { useCustomPreferences } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';

export const RECAPTCHA_ENTERPRISE_SCRIPT_URL = 'https://www.google.com/recaptcha/enterprise.js';

export interface UseRecaptchaScriptLoaderResult {
    isRecaptchaEnabled: boolean;
    scriptStatus: UseScriptStatus;
}
export const useRecaptchaScriptLoader = (): UseRecaptchaScriptLoaderResult => {
    const {
        env: { recaptchaEnterpriseV3SiteKey },
    } = useSLTConfig();
    const preferences = useCustomPreferences([]);

    const isRecaptchaEnabled = useMemo(() => {
        const recaptchaEnabledPreferences = [
            CustomPreference.RECAPTCHA_ACCOUNT_LOGIN_ENABLED,
            /* TODO: Uncomment when recaptcha is implemented in these features
            CustomPreference.RECAPTCHA_ACCOUNT_REGISTRATION_ENABLED,
            CustomPreference.RECAPTCHA_FORGOT_PASSWORD_ENABLED,
            CustomPreference.RECAPTCHA_PASSWORD_RESET_ENABLED,
            CustomPreference.RECAPTCHA_TRACK_ORDER_ENABLED,
            */
            CustomPreference.RECAPTCHA_GIFT_CARD_BALANCE_ENABLED,
            CustomPreference.RECAPTCHA_PROMO_CODE_ENABLED,
            /* TODO: Uncomment when recaptcha is implemented in these features
            CustomPreference.RECAPTCHA_ADD_PAYMENT_METHOD_ENABLED,
            CustomPreference.RECAPTCHA_PLACE_ORDER_ENABLED,
            */
        ];

        return recaptchaEnabledPreferences.some((pref) => {
            const value = preferences[pref];
            return value === 'true' || value === true;
        });
    }, [preferences]);

    const scriptUrl = useMemo(() => {
        if (!isRecaptchaEnabled || !recaptchaEnterpriseV3SiteKey) {
            return null;
        }
        return `${RECAPTCHA_ENTERPRISE_SCRIPT_URL}?render=${recaptchaEnterpriseV3SiteKey}`;
    }, [isRecaptchaEnabled, recaptchaEnterpriseV3SiteKey]);

    const scriptStatus = useScript(scriptUrl || '', {
        removeOnUnmount: false,
    });

    return {
        isRecaptchaEnabled,
        scriptStatus,
    };
};
